import { HighlightWrapper } from "react-css-highlight";
import { FormControl, InputLabel, OutlinedInput, Select } from "@mui/material";
import { selectClasses } from "@mui/material/Select";
import {
  FORM_CONTROL_STYLES,
  MENU_PROPS,
  SELECT_LABEL_ID,
  SELECT_SELECT_STYLES,
  SELECT_STYLES,
  SELECT_TEST_ID,
  SELECTED_COUNT_THRESHOLD_FOR_LABEL,
} from "./AssetsFilterTags.constants";
import {
  useAssetsFilterTags,
  useTagFamilyExpansion,
  useTagSearch,
  useTagSelection,
} from "./AssetsFilterTags.hooks";
import AdditionalTagsList from "./components/AdditionalTagsList/AdditionalTagsList";
import EmptySearchState from "./components/EmptySearchState/EmptySearchState";
import SelectedTagsChips from "./components/SelectedTagsChips/SelectedTagsChips";
import StandaloneTagsList from "./components/StandaloneTagsList/StandaloneTagsList";
import TagFamilySection from "./components/TagFamilySection/TagFamilySection";
import TagSearchHeader from "./components/TagSearchHeader/TagSearchHeader";

export default function AssetsFilterTags() {
  const { tags, appTags, setAppTags } = useAssetsFilterTags();
  const {
    searchText,
    setSearchText,
    showSelectedOnly,
    setShowSelectedOnly,
    resetSearchUi,
    relevantTags,
    appTagsSet,
    familyMap,
    standaloneTagsArray,
    familyNamesArray,
  } = useTagSearch(tags, appTags);
  const { openFamilies, toggleFamily } = useTagFamilyExpansion(
    searchText,
    familyNamesArray,
    showSelectedOnly
  );
  const { toggleTag, clearAll } = useTagSelection(appTags, setAppTags);

  const selectedCount = appTags.length;
  const labelText =
    selectedCount > SELECTED_COUNT_THRESHOLD_FOR_LABEL
      ? `Tags (${selectedCount})`
      : "Tags";

  return (
    <FormControl sx={FORM_CONTROL_STYLES} size="small">
      <InputLabel>{labelText}</InputLabel>
      <Select
        labelId={SELECT_LABEL_ID}
        data-ph-capture-attribute-action="filter_tags"
        data-test-id={SELECT_TEST_ID}
        multiple
        value={appTags}
        sx={{
          ...SELECT_STYLES,
          [`& .${selectClasses.select}`]: {
            ...SELECT_SELECT_STYLES,
            ...(selectedCount > 0 && { py: 0.8 }),
          },
        }}
        input={<OutlinedInput label={labelText} />}
        renderValue={(selected) => (
          <SelectedTagsChips
            selectedTagIds={selected}
            allTags={tags}
            onRemoveTag={(tagId) => setAppTags(appTags.filter((id) => id !== tagId))}
          />
        )}
        onClose={resetSearchUi}
        MenuProps={MENU_PROPS}
      >
        <TagSearchHeader
          searchText={searchText}
          onSearchChange={setSearchText}
          selectedCount={selectedCount}
          onClearAll={clearAll}
          tagsCount={tags?.length ?? 0}
          showSelectedOnly={showSelectedOnly}
          onShowSelectedOnlyChange={setShowSelectedOnly}
        />

        <EmptySearchState
          searchText={searchText}
          hasResults={!!relevantTags?.length}
        />

        <AdditionalTagsList selectedTagIds={appTags} onToggle={toggleTag} />

        <HighlightWrapper search={searchText}>
          <div>
            <StandaloneTagsList
              tags={standaloneTagsArray}
              selectedTagIds={appTagsSet}
              onToggle={toggleTag}
              searchText={searchText}
            />

            {[...familyMap.keys()].map((familyName) => (
              <TagFamilySection
                key={familyName}
                familyName={familyName}
                tags={familyMap.get(familyName)!}
                isOpen={openFamilies.has(familyName)}
                onToggle={() => toggleFamily(familyName)}
                selectedTagIds={appTagsSet}
                onTagToggle={toggleTag}
                searchText={searchText}
              />
            ))}
          </div>
        </HighlightWrapper>
      </Select>
    </FormControl>
  );
}
